import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { SupportRequestBackendClient } from '@naus-code/naus-admin-types';
import { LinkText } from '~/components/LinkText';
import { translate } from '~/utils/translation.utils';
import { useVirtualInventoryPayload } from '../Modals/VirtualInventoryPayload';
import { getVIOptionsFromSR } from '../utils/SupportRequestData';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { useModalVerify } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { useGetAuthUserRole, useGetAuthUserRules } from '~/screens/Auth/Auth.Context';
import { DATE_TIME_SERVER_FORMAT } from '~/utils/date.utils';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { AppText } from '~/components/Text';
import { useIsSystemUser } from '~/hooks/auth.hook';

export const VIRequestOptions = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const { open: openVirtualInventoryPayload } = useVirtualInventoryPayload();
  const viOptions = getVIOptionsFromSR(supportRequestInfo);
  const { verifyAction } = useModalVerify();

  const booking = bookingApi.useGetBookingQuery(supportRequestInfo.bookingId || '', {
    skip: !supportRequestInfo.bookingId || !viOptions,
  });
  const canSeeCancel = useCanCancelVIManually();

  const [cancelVIBooking, { isLoading: isCancelling }] =
    supportRequestActionsApi.useCancelVIBookingMutation();

  const [overrideConfirmVIBooking, { isLoading: isConfirming }] =
    supportRequestActionsApi.useOverrideConfirmVIBookingMutation();

  const canShowConfirmOverride = useIsSystemUser();
  // supportRequestInfo.supportHistory.some(
  //   (history) => history.type === 'PENDING_CAPTURE_FAIL',
  // ) || useIsSystemUser();

  if (!viOptions || !booking.data?.booking.hasPending) {
    return null;
  }
  const hasPayload = viOptions?.viPayload || viOptions?.errorPayload;

  return (
    <VStack space="2.5" px="3">
      <AppText.Body1Regular>{`${translate('supportRequests.virtualInventory.validUntil')} ${dayjs(viOptions?.validUntil).format(DATE_TIME_SERVER_FORMAT)}`}</AppText.Body1Regular>
      {hasPayload && (
        <VStack
          flex={1}
          space="2"
          align="center"
          bg="primary-extraLight"
          p="2.5"
          b={1}
          bc="primary-light"
          br={8}
        >
          <LinkText
            textStyle={{ fontSize: 15 }}
            onPress={() => {
              if (viOptions) {
                openVirtualInventoryPayload(supportRequestInfo, check);
              }
            }}
          >
            {translate('supportRequests.virtualInventoryPayload')}
          </LinkText>
        </VStack>
      )}
      {canSeeCancel && (
        <VStack
          flex={1}
          space="2"
          align="center"
          bg="error-extraLight"
          p="2.5"
          b={1}
          bc="error-light"
          br={8}
        >
          <LinkText
            textStyle={{ fontSize: 15, color: 'red' }}
            isLoading={isCancelling}
            onPress={async () => {
              verifyAction({
                title: translate('supportRequests.virtualInventory.cancelBooking'),
                description: translate(
                  'supportRequests.virtualInventory.cancelBookingDescription',
                ),
              }).then(async (res) => {
                if (res) {
                  await cancelVIBooking({
                    check,
                    supportId: supportRequestInfo._id,
                  }).then((response) => {
                    handleResponse({
                      response,
                    });
                  });
                }
              });
            }}
          >
            {translate('supportRequests.cancelVirtualInventoryRequest')}
          </LinkText>
        </VStack>
      )}
      {canShowConfirmOverride && (
        <VStack
          flex={1}
          space="2"
          align="center"
          bg="success-extraLight"
          p="2.5"
          b={1}
          bc="success-light"
          br={8}
        >
          <LinkText
            textStyle={{ fontSize: 15, color: 'green' }}
            isLoading={isConfirming}
            onPress={async () => {
              verifyAction({
                title: translate('supportRequests.virtualInventory.confirmBooking'),
                description: translate(
                  'supportRequests.virtualInventory.confirmBookingDescription',
                ),
              }).then(async (res) => {
                if (res) {
                  await overrideConfirmVIBooking({
                    check,
                    supportId: supportRequestInfo._id,
                  }).then((response) => {
                    handleResponse({
                      response,
                    });
                  });
                }
              });
            }}
          >
            {translate('supportRequests.confirmVirtualInventoryRequest')}
          </LinkText>
        </VStack>
      )}
      <Divider />
    </VStack>
  );
};

const useCanCancelVIManually = () => {
  const userRole = useGetAuthUserRole();
  const canCancelVIManually = useGetAuthUserRules('canCancelVIManually');
  if (
    canCancelVIManually ||
    userRole === 'CUSTOMER_SUPPORT_LEAD' ||
    userRole === 'ADMIN' ||
    userRole === 'SYSTEM'
  ) {
    return true;
  }
  return false;
};
